import { reactive, ref, toRefs, computed, nextTick } from 'vue'
import moment from 'moment'
import { Message } from 'view-design'
import { PLATFORM } from '@/common/js/constants.js'
import { groupBy, clone, pick } from '@/common/js/just.js'
import {
  AMEME_TYPE,
  DELIVERY_MODE,
  TOUTIAO_MENU_ID,
  TOUTIAO_V3_MENU_ID,
  AD_TYPE,
  DELIVERY_TYPE,
  LANDING_TYPE,
  ANCHOR_RELATED_TYPE,
  MATERIAL_STYLE,
  MARKETING_GOAL,
  PROJECT_AUDIENCE_TYPE
} from '../consts/index'
import {
  INVENTORY_CATALOG,
  INVENTORY_TYPE,
  PLATFORM as TARGETING_PLATFORM
} from '@/views/promote/targeting/model/toutiao_targeting_consts.js'
import { getSubmitTargetingParams, proceduralTargetings, searchAdTargetings } from './utils/index.js'
import { buildBasicPlanRow, getCreativeImageMode } from './utils/plan.js'
import { fillBatchParams } from './utils/fillBatchParams.js'
import { getConfigFormModel } from '../model/form.js'
import { getDefaultCheckStates } from '../components/preview-table/useTablePreview.js'

import { submitNewToutiaoBatchTask, submitToutiaoJubaoBatchTask } from '@/api/toutiao.local.model'
import { addtemplate } from '@/api/ptom.data.model'
import { getTemplateConditionInfo } from './useToutiaoPlanTemplates.js'
import { MATERIAL_TYPE } from '@/components/material-selector/consts'

export const usePlanDistribute = (configForm) => {
  const formColRefs = ref([])
  const previewModel = reactive({
    activeAccount: '',
    submitLoading: false,
    submitConfirmModal: false,
    submitSaveTemplateName: '',
    duplicateProjectNameError: '',
    duplicatePlanNameError: '',
    // 根据账号分组的预览表格状态表单，管理表头的全局switch状态
    //  {
    //    [account.id]: {
    //     projectSwitch: true,
    //     planSwitch: true
    //   }
    //  }
    tableStatesForm: {},
    previewOriginData: {},
    targetPackages: [],
    creativeGroups: []
  })
  const tableData = computed({
    get() {
      return previewModel.previewOriginData[previewModel.activeAccount] || []
    },
    set(data) {
      previewModel.previewOriginData[previewModel.activeAccount] = data
    }
  })

  const updateTableData = (data) => {
    tableData.value = data
  }

  const updateTableForm = (data) => {
    previewModel.tableStatesForm[previewModel.activeAccount] = data
  }

  const tableFormFilteredByAccount = computed({
    get() {
      return previewModel.tableStatesForm[previewModel.activeAccount]
    },
    set(data) {
      previewModel.tableStatesForm[previewModel.activeAccount] = data
    }
  })

  const onPlansValidate = async () => {
    const { project, targetings, plan, creatives, descriptions, landingPages } = configForm.value
    if (Object.keys(project).length === 0) {
      Message.warning('请先配置项目信息')
      return Promise.reject(new Error('请先配置项目信息'))
    }
    const isUnlimitedV3 = configForm.value.isV3 && project.audience_type === PROJECT_AUDIENCE_TYPE.UNLIMITED
    if (targetings.length === 0 && !isUnlimitedV3) {
      Message.warning('请先配置定向信息')
      return Promise.reject(new Error('请先配置定向信息'))
    }
    if (Object.keys(plan).length === 0) {
      Message.warning('请先配置计划信息')
      return Promise.reject(new Error('请先配置计划信息'))
    }

    if (plan.materials_type === MATERIAL_STYLE.PROMOTION_MATERIALS) {
      if (creatives.length === 0) {
        Message.warning('请先配置创意素材')
        return Promise.reject(new Error('请先配置创意素材'))
      }
      if (descriptions.length === 0) {
        Message.warning('请先配置文案包')
        return Promise.reject(new Error('请先配置文案包'))
      }
    }
    if (project.marketing_goal === MARKETING_GOAL.LIVE) {
      // todo
    } else {
      if ([PLATFORM.ANDROID].includes(project.ch_platform_id)) {
        if (landingPages.length === 0) {
          Message.warning('请先配置落地页包')
          return Promise.reject(new Error('请先配置落地页包'))
        }
      }
    }

    try {
      for (let i = 0; i < formColRefs.value.length; i++) {
        const ref = formColRefs.value[i]
        if (ref) {
          await ref.validate()
        }
      }

      // 每次预览时清空重名错误提示
      previewModel.duplicateProjectNameError = ''
      previewModel.duplicatePlanNameError = ''

      return Promise.resolve()
    } catch (e) {
      console.log('onPlansPreview valid eeeeeee output: ', e)
      return Promise.reject(new Error(JSON.stringify({ index: e.message })))
    }
  }

  /**
   * @description: 生成计划预览表格数据
   * @return {*}
   */
  const onPlansPreview = async () => {
    const previewConfigForm = getPreviewConfigForm(configForm.value)
    previewModel.activeAccount = previewConfigForm.accounts[0].id
    previewModel.targetPackages = buildTargetingBasedPlanRows(previewConfigForm).map((p) => p.targetings)
    previewModel.creativeGroups = previewConfigForm.creatives

    const { project, template } = previewConfigForm
    const { adNumber, accountAllocation } = template
    // 广告数量：账号 × 定向包数 × 创意组数
    if (adNumber === 1) {
      // 以定向包为基准，创建广告计划
      let planList = buildTargetingBasedPlanRows(previewConfigForm)

      // 分配创意
      planList = multiplyMaterials(planList, previewConfigForm)
      // 分配文案
      planList = appendDescriptions(planList, previewConfigForm)
      planList = appendLandingPages(planList, previewConfigForm)
      // 根据定向包分配项目，一个定向生成一个项目
      const planGroups = groupBy(planList, (plan) => plan.targetings.caohuaTargetingId)

      // 多账号广告分配：全部复制
      if (accountAllocation === 1) {
        previewModel.previewOriginData = distributeByDupItemToAccount(Object.values(planGroups), previewConfigForm)
      }
    }

    if (adNumber === 2) {
      // 以定向包为基准，创建广告计划
      let planList = buildTargetingBasedPlanRows(previewConfigForm)

      planList = multiplyMaterials(planList, previewConfigForm)
      planList = appendDescriptions(planList, previewConfigForm)
      planList = appendLandingPages(planList, previewConfigForm)

      // 多账号：按定向分配
      if (accountAllocation === 2) {
        previewModel.previewOriginData = distributeByTargeting(planList, previewConfigForm)
      }

      // 多账号：按创意组分配
      if (accountAllocation === 3) {
        const planGroupByTargeting = groupBy(planList, (plan) => plan.targetings.caohuaTargetingId)

        // 根据创意组分配项目，一个创意+定向点个数，生成一个项目
        const planGroups = groupBy(planList, (plan) => plan.creative_params.campaign_name)

        previewModel.previewOriginData = distributeByCreative(
          Object.values(planGroups),
          Object.values(planGroupByTargeting),
          previewConfigForm
        )
      }
    }

    // 最终提交的计划数 = 创意组数
    if (adNumber === 3) {
      if (accountAllocation === 3) {
        const { accounts, targetings, creatives } = previewConfigForm
        if (accounts.length * targetings.length > creatives.length) {
          const msg = `创意组数不能小于${accounts.length * targetings.length}个`
          Message.warning(msg)
          return Promise.reject(new Error(msg))
        }
      }
      // 以创意为基准，创建广告计划
      let planList = buildCreativeBasedPlanRow(previewConfigForm)
      planList = appendDescriptions(planList, previewConfigForm)
      planList = appendLandingPages(planList, previewConfigForm)
      previewModel.previewOriginData = distributeByCreativeGroup(accountAllocation, planList, previewConfigForm)
    }

    if (previewConfigForm.isV3) {
      previewModel.previewOriginData = normalizeV3PlanRows(previewModel.previewOriginData)
    }

    for (const i in previewModel.previewOriginData) {
      const tableData = previewModel.previewOriginData[i].map((tableRow) => {
        return {
          ...tableRow,
          // 初始化预览每行的check状态
          ...getDefaultCheckStates()
        }
      })
      previewModel.previewOriginData[i] = tableData
    }
    previewModel.tableStatesForm = tableStatesFormToAccount(previewConfigForm)
    nextTick(() => {
      document.querySelector('.preview-table').scrollIntoView({ block: 'start', behavior: 'smooth' })
    })
  }

  function duplicateNameValidate() {
    previewModel.duplicatePlanNameError = ''
    previewModel.duplicateProjectNameError = ''
    const planData = Object.values(previewModel.previewOriginData).flat()
    // 已存在的广告名称
    const projectSeen = new Set()
    const planSeen = new Set()
    // 重复的计划
    const projectDuplicates = new Set()
    const planDuplicates = new Set()

    // 项目名称分组，每个项目组只取第一个广告进行项目名称是否重复判断
    const projectData = Object.values(groupBy(planData, (plan) => plan.temp_project_name)).map((i) => i[0])

    planData.forEach((i) => {
      const planName = i.inner_plan_params.plan_name
      if (planSeen.has(planName)) {
        planDuplicates.add(i)
      } else {
        planSeen.add(planName)
      }
    })

    projectData.forEach((i) => {
      const projectName = i.campaign_params.project_name
      if (projectSeen.has(projectName)) {
        projectDuplicates.add(i)
      } else {
        projectSeen.add(projectName)
      }
    })

    if (projectDuplicates.size > 0) {
      const [errorRow] = [...projectDuplicates]
      previewModel.duplicateProjectNameError = errorRow.campaign_params.project_name
      return Promise.reject(new Error('存在重复的项目名称'))
    }

    if (planDuplicates.size > 0) {
      const [errorRow] = [...planDuplicates]
      previewModel.duplicatePlanNameError = errorRow.inner_plan_params.plan_name
      return Promise.reject(new Error('存在重复的计划名称'))
    }

    return Promise.resolve()
  }

  const onSubmitCancel = () => {
    previewModel.submitConfirmModal = false
    previewModel.submitSaveTemplateName = ''
  }

  const onSubmitValidate = async () => {
    try {
      await duplicateNameValidate()
      const planData = Object.values(previewModel.previewOriginData).flat()
      for (const plan of planData) {
        const { ch_platform_id, plan_name } = plan.inner_plan_params
        const { marketing_goal } = plan.outer_plan_params
        const { native_setting, anchor_name } = plan.creative_params
        if (!plan.campaign_params.project_name.trim()) {
          throw new Error('项目名称不能为空')
        }

        if (!plan_name.trim()) {
          throw new Error('计划名称不能为空')
        }

        // 直播场景安卓、鸿蒙不需要配置落地页
        if (marketing_goal === MARKETING_GOAL.LIVE && [PLATFORM.ANDROID, PLATFORM.HARMONY].includes(ch_platform_id)) {
          // todo
        } else {
          if ([PLATFORM.ANDROID, PLATFORM.MINI_PROGRAM, PLATFORM.HARMONY].includes(ch_platform_id)) {
            if (!plan.creative_params.web_url_material_list?.length) {
              previewModel.activeAccount = plan.ad_account_id
              throw new Error(`${plan_name}须配置落地页`)
            }
          }
        }

        if (native_setting?.anchor_related_type === ANCHOR_RELATED_TYPE.SELECT) {
          if (!anchor_name) {
            previewModel.activeAccount = plan.ad_account_id
            throw new Error(`${plan_name}须配置锚点`)
          }
        }
      }
    } catch (e) {
      return Message.warning(e.message)
    }
    previewModel.submitConfirmModal = true
  }

  /**
   * @description: 确认提交
   * @return {*}
   */
  const onSubmitConfirm = async (saveTemplateData) => {
    const planlist = Object.values(previewModel.previewOriginData)
      .flat()
      .filter((plan) => plan.isCheck !== false)
      .map((row) => {
        const { ad_account_id, campaign_params, creative_params, inner_plan_params, outer_plan_params, targetings } = clone(row)
        const submitTargetings = configForm.value.isV3 ? normalizeV3SubmitTargetings(targetings) : targetings
        const newPlan = {
          ad_account_id,
          campaign_params,
          creative_params,
          inner_plan_params,
          outer_plan_params,
          targetings: submitTargetings
        }
        if (configForm.value.isV3) {
          newPlan.inner_plan_params.plan_name = newPlan.campaign_params.project_name
          newPlan.outer_plan_params.delivery_type = DELIVERY_TYPE.NORMAL
        }
        newPlan.outer_plan_params.operation = previewModel.tableStatesForm[newPlan.ad_account_id].projectSwitch ? 'ENABLE' : 'DISABLE'
        // 去除辅助参数：落地页包名、标题包名、预览图
        const { web_url_pack_name, title_pack_name, preview_list, ...restParams } = newPlan.creative_params
        restParams.operation = previewModel.tableStatesForm[newPlan.ad_account_id].planSwitch ? 'ENABLE' : 'DISABLE'
        newPlan.creative_params = restParams

        return newPlan
      })
    previewModel.submitLoading = true
    try {
      const submitBatchTask = configForm.value.isV3 ? submitToutiaoJubaoBatchTask : submitNewToutiaoBatchTask
      const data = await submitBatchTask({ operation: 'ENABLE', planlist })
      if (data) {
        Message.success('提交成功！')

        if (saveTemplateData?.menuId) {
          await addtemplate(saveTemplateData)
        }

        previewModel.previewOriginData = {}
        // configForm.value = getConfigFormModel()
      }

      previewModel.submitLoading = false
      onSubmitCancel()
    } catch (e) {
      previewModel.submitLoading = false
      onSubmitCancel()
    }
  }

  const saveTemplateSubmit = async () => {
    try {
      const data = {
        menuId: configForm.value.isV3 ? TOUTIAO_V3_MENU_ID : TOUTIAO_MENU_ID,
        name: previewModel.submitSaveTemplateName.trim(),
        conditionInfo: getTemplateConditionInfo(configForm.value)
      }
      onSubmitConfirm(data)
    } catch (e) {
      console.log('saveTemplateSubmit eeeeeeeeeee output: ', e)
    }
  }

  return {
    ...toRefs(previewModel),
    formColRefs,
    tableData,
    tableFormFilteredByAccount,
    updateTableData,
    updateTableForm,
    onPlansValidate,
    onPlansPreview,
    onSubmitValidate,
    duplicateNameValidate,
    onSubmitCancel,
    onSubmitConfirm,
    saveTemplateSubmit
  }
}

function getPreviewConfigForm(configForm) {
  const isUnlimitedV3 = configForm.isV3 && configForm.project.audience_type === PROJECT_AUDIENCE_TYPE.UNLIMITED
  if (!isUnlimitedV3 || configForm.targetings.length) return configForm

  return {
    ...configForm,
    targetings: [
      {
        targetingId: null,
        targetingName: '不限',
        targetingParamJson: null
      }
    ]
  }
}

function normalizeV3PlanRows(plansByAccount) {
  const nameCounts = new Map()
  let rowIndex = 0

  return Object.keys(plansByAccount).reduce((result, accountId) => {
    result[accountId] = plansByAccount[accountId].map((row) => {
      const plan = clone(row)
      const baseName = plan.campaign_params.project_name
      const count = (nameCounts.get(baseName) || 0) + 1
      const projectName = count === 1 ? baseName : `${baseName}_${count}`
      nameCounts.set(baseName, count)
      rowIndex += 1

      plan.campaign_params.project_name = projectName
      plan.inner_plan_params.plan_name = projectName
      plan.temp_project_name = `toutiaov3_${rowIndex}`
      delete plan.planRowDisabled
      return plan
    })
    return result
  }, {})
}

function buildCreativeBasedPlanRow(configForm) {
  const { creatives, plan: planItem } = configForm

  const planList = creatives.map((creative) => {
    const plan = buildBasicPlanRow(configForm)

    const dupPlan = appendMaterialImageList(plan, creative)

    // return fillBatchParams(dupPlan, planItem, creative)
    return dupPlan
  })

  return planList
}

/**
 * @description: 以定向包为基准创建广告计划
 * @return {*}
 */
export function buildTargetingBasedPlanRows(configForm) {
  const { targetings } = configForm

  const planList = targetings.map((t) => {
    const plan = buildBasicPlanRow(configForm)

    const delivery_range = buildDeliveryRange(t, plan)
    const { delivery_mode, delivery_type, ad_type } = plan.outer_plan_params
    const delivery = ad_type === AD_TYPE.SEARCH
      ? (configForm.isV3 ? DELIVERY_TYPE.NORMAL : delivery_type)
      : delivery_mode
    const targetingParam = filterTargetingParams(t.targetingParamJson, ad_type, delivery, configForm.isV3)
    const innerPlanParams = { ...plan.inner_plan_params }
    if (t.targetingId) innerPlanParams.caohuaTargetingId = t.targetingId

    return {
      ...plan,
      targetings: {
        caohuaTargetingId: t.targetingId,
        // 定向名称，用于预览表格显示
        targetingName: t.targetingName,
        targetingParam
      },
      inner_plan_params: innerPlanParams,
      outer_plan_params: {
        ...plan.outer_plan_params,
        delivery_range
      }
    }
  })
  return planList
}

export function normalizeV3SubmitTargetings(targetings = {}) {
  const { caohuaTargetingId, targetingName, ...submitTargetings } = targetings
  if (caohuaTargetingId) submitTargetings.targetingId = caohuaTargetingId
  return submitTargetings
}

/**
 * @description: 小程序推广目的定向：平台切换
 * @param {*} targetingParamJson
 * @param {*} delivery
 * @return {*}
 */
function getMiniProgramSubmitTargetingParams(json, landing_type) {
  // 如果是小程序投放，且推广目的为应用app（微端）时，定向切换到安卓
  if (landing_type === LANDING_TYPE.APP) {
    json.platform = TARGETING_PLATFORM.ANDROID
  }
  // 如果是小程序投放，定向切换到不限
  if (landing_type === LANDING_TYPE.MICRO_GAME) {
    json.platform = TARGETING_PLATFORM.UNLIMITED
  }
  return getSubmitTargetingParams(json)
}

/**
 * @description: 根据广告类型、投放方式，过滤定向参数
 * @param {*} targetingParamJson
 * @param {*} delivery
 * @return {*}
 */
function filterTargetingParams(targetingParamJson, adType, delivery, isV3 = false) {
  if (isV3 && !targetingParamJson) return {}
  const submitParams = getSubmitTargetingParams(targetingParamJson, { isV3 })

  if (adType === AD_TYPE.ALL) {
    if (delivery === DELIVERY_MODE.PROCEDURAL) {
      // 选取自动投放支持的定向参数、并清除人群定向参数
      const proceduralTargetingsParams = pick(clone(submitParams), proceduralTargetings)
      if (!isV3 && proceduralTargetingsParams.audiences?.retargeting_tags_include) {
        proceduralTargetingsParams.audiences.retargeting_tags_include = []
      }

      return proceduralTargetingsParams
    }
    return submitParams
  }

  if (adType === AD_TYPE.SEARCH) {
    // 选取搜索广告支持的定向参数
    const searchAdTargetingsParams = pick(clone(submitParams), searchAdTargetings)
    // 搜索广告极速智投去掉设备品牌定向
    const { device_brand, ...durationTargetingParams } = searchAdTargetingsParams
    return delivery === DELIVERY_TYPE.DURATION ? durationTargetingParams : searchAdTargetingsParams
  }
}

/**
 * @description: 创意参数填充素材数据，包括图片、视频、音频；添加批量复制流程回显创意的辅助参数、添加广告计划预览创意的辅助参数
 * @param {*} plan
 * @param {*} creative
 * @return {*}
 */
export function appendMaterialImageList(plan, creative) {
  const dupPlan = clone(plan)
  const { delivery_type } = dupPlan.outer_plan_params
  const isV3 = dupPlan.inner_plan_params.media_version === 'TOUTIAO3.0'
  const { image, video, imageAudio } = creative.selectedMaterials
  dupPlan.creative_params.campaign_name = creative.creative_name
  // 为后端传入动态词【广告组名】
  // dupPlan.creative_params.creative_name = creatives[0].creative_name
  dupPlan.creative_params.preview_list = []
  // 极速智投，不需要logo
  // if (delivery_type === DELIVERY_TYPE.DURATION) {
  //   dupPlan.creative_params.image_list = []
  // } else {
  //   // 非极速智投，添加app logo
  //   dupPlan.creative_params.image_list = [
  //     {
  //       image_mode: 'PRODUCT_LOGO',
  //       image_ids: dupPlan.creative_params.product_info.image_ids
  //     }
  //   ]
  // }

  // 直播素材不需要传入素材列表
  if (dupPlan.plan.materials_type === MATERIAL_STYLE.LIVE_MATERIALS) {
    dupPlan.creative_params.image_material_list = []
    dupPlan.creative_params.video_material_list = []
    return dupPlan
  }
  const [logoId] = dupPlan.creative_params.product_info.image_ids
  const materials = [
    {
      key: `@${logoId}`,
      type: 'LOGO',
      inner_id: `${logoId}`
    }
  ]
  // 把product_info内的image_ids修改成@开头
  dupPlan.creative_params.product_info.image_ids = [`@${logoId}`]

  // 图片
  if (image?.length) {
    for (let j = 0; j < image.length; j++) {
      const imageItem = image[j]
      const imageMode = getCreativeImageMode(imageItem)
      if (Object.keys(imageMode).length > 0) {
        const imageMaterial = isV3
          ? { image_mode: imageMode.image_mode, image_id: imageMode.images[0].image_id }
          : imageMode
        dupPlan.creative_params.image_material_list.push(imageMaterial)

        materials.push({
          key: `@${imageItem.id}`,
          type: 'IMAGE',
          inner_id: `${imageItem.id}`
        })
      }

      dupPlan.creative_params.preview_list.push({
        // 添加地址和横版竖版信息，用于表格显示
        name: imageItem.name,
        badge: '图片',
        url: imageItem.preview_url,
        vertical: imageItem.height > imageItem.width
      })
    }
  }

  // 原生广告设置：达人视频
  if (dupPlan.creative_params.awemeIdentity === AMEME_TYPE.AWEME_VIDEO) {
    // aweme_setting_type = SINGLE 时，请传入aweme_id，表示选择单个抖音号
    // aweme_setting_type = MULTIPLE 时，请传入aweme_ids，表示选择多抖音号 （≠全选）
    // aweme_setting_type = ALL 时，无需传入aweme_id 或 aweme_ids（传入不生效），后续星广联投任务下新增投稿达人将被自动选中

    // 20250626 不管aweme_setting_type是ALL还是其他，前端始终传入aweme_id，由后端提交给媒体时进行判断删除
    dupPlan.creative_params.native_setting.aweme_id = creative.dyAccount

    dupPlan.creative_params.native_setting.aweme_name = creative.selectedMaterials.video
      ? creative.selectedMaterials.video[0].aweme_name
      : creative.dyAccount
    for (let j = 0; j < video.length; j++) {
      const videoItem = video[j]
      dupPlan.creative_params.video_material_list.push({
        item_id: videoItem.video_info.item_id,
        video_id: videoItem.video_info.video_id,
        video_cover_id: videoItem.video_info.video_cover_id,
        image_mode: videoItem.video_info.image_mode,
        auth_type: 'VIDEO_ITEM'
      })
      dupPlan.creative_params.preview_list.push({
        // 添加地址和横版竖版信息，用于表格显示
        name: videoItem.video_info.title,
        badge: '视频',
        url: videoItem.preview_url, // 视频封面图
        vertical: videoItem.vertical
      })
    }
  } else if (video?.length) {
    // 视频
    for (let j = 0; j < video.length; j++) {
      const videoItem = video[j]
      const { cover_replaced_type } = videoItem
      const imageMode = getCreativeImageMode(videoItem)

      if (Object.keys(imageMode).length > 0) {
        dupPlan.creative_params.video_material_list.push(imageMode)
        materials.push({
          key: `@${videoItem.id}`,
          type: 'VIDEO',
          inner_id: `${videoItem.id}`
        })
        materials.push({
          key: `@${videoItem.cover_id}`,
          type: 'IMAGE',
          inner_id: `${videoItem.cover_id}`,
          // 如果选择的是图片作为封面，那么认为封面被替换过，如果只是替换另一个默认封面，则不认为替换了封面
          // 没有cover_replaced_type，那就是没有替换过，defaultCover为true
          defaultCover: !cover_replaced_type || videoItem.cover_replaced_type === MATERIAL_TYPE.POSTER
        })
      }
      dupPlan.creative_params.preview_list.push({
        // 添加地址和横版竖版信息，用于表格显示
        name: videoItem.name,
        badge: '视频',
        url: videoItem.preview_url, // 视频封面图
        vertical: videoItem.vertical ? videoItem.vertical : videoItem.height > videoItem.width
      })
    }
  }

  // 图文
  if (imageAudio?.length) {
    const carousel_material_list = []
    for (let j = 0; j < imageAudio.length; j++) {
      const imageAudioItem = imageAudio[j]
      carousel_material_list.push({
        carousel_id: imageAudioItem.id
      })
      dupPlan.creative_params.preview_list.push({
        // 添加地址和横版竖版信息，用于表格显示
        name: imageAudioItem.name,
        badge: '图文',
        url: imageAudioItem.preview_url,
        vertical: imageAudioItem.height > imageAudioItem.width
      })
    }
    dupPlan.creative_params.carousel_material_list = carousel_material_list
  }
  if (materials?.length) {
    dupPlan.creative_params.materials = materials
  }
  return dupPlan
}

/**
 * @description: 为基础计划参数乘以素材数，得到计划数
 * @return {*}
 */
function multiplyMaterials(planList, { creatives, plan: planItem }) {
  let newPlanList = []
  for (let i = 0; i < creatives.length; i++) {
    const creative = creatives[i]
    const newList = planList.map((row) => appendMaterialImageList(row, creative))
    newPlanList = newPlanList.concat(newList)
  }
  return newPlanList
}

/**
 * @description: 为计划分配文案参数
 * @return {*}
 */
function appendDescriptions(planList, { descriptions, project, plan, isV3 }) {
  // 关联星广联投任务且开启了素材优化不需要提交文案
  const { star_task, star_auto_material_addition_switch } = project
  if (!isV3 && star_task?.star_task_id && star_auto_material_addition_switch === 'ON' && plan.original_video_title === 'ON') {
    return planList
  }

  const isLiveMaterial = plan.materials_type === MATERIAL_STYLE.LIVE_MATERIALS
  // 直播素材不需要文案
  if (isLiveMaterial) {
    return planList
  }

  // 遍历文案包，为每个计划设置文案
  return planList.map((plan, index) => {
    const dupPlan = clone(plan)
    const descIndex = index % descriptions.length
    // 文案最多10条
    const descItems = descriptions[descIndex]?.content?.slice(0, 10) || []
    const packName = descriptions[descIndex].packName
    dupPlan.creative_params.title_pack_name = packName
    if (descItems?.length) {
      dupPlan.creative_params.title_material_list = descItems.map((item) => ({ title: item }))
    }

    return dupPlan
  })
}

function buildDeliveryRange(targeting, plan) {
  const { inventory_catalog, inventory_type, union_video_type } = targeting.targetingParamJson || {}
  // 从定向包中取出版位参数，放到外部计划参数中
  const delivery_range = { inventory_catalog, inventory_type }
  const { delivery_mode, ad_type } = plan.outer_plan_params

  if (ad_type === AD_TYPE.ALL) {
    // 自动投放模式，覆盖广告位置，只能选择通投智选
    if (delivery_mode === DELIVERY_MODE.PROCEDURAL) {
      delivery_range.inventory_catalog = INVENTORY_CATALOG.UNIVERSAL_SMART
      delivery_range.inventory_type = [INVENTORY_TYPE.INVENTORY_UNIVERSAL]
    }

    if (delivery_mode === DELIVERY_MODE.MANUAL) {
      // 投放形式（穿山甲视频创意类型）inventory_catalog = MANUAL && inventory_type 仅有 INVENTORY_UNION_SLOT 时 有效且必填；
      if (
        inventory_catalog === INVENTORY_CATALOG.MANUAL &&
        inventory_type.length === 1 &&
        inventory_type[0] === INVENTORY_TYPE.INVENTORY_UNION_SLOT
      ) {
        delivery_range.union_video_type = union_video_type
      }
    }
  }

  if (ad_type === AD_TYPE.SEARCH) {
    // 当 ad_type=SEARCH时，仅支持 MANUAL首选媒体
    delivery_range.inventory_catalog = INVENTORY_CATALOG.MANUAL
    // 当 ad_type=SEARCH搜索广告时，仅支持 INVENTORY_SEARCH 搜索广告;
    delivery_range.inventory_type = [INVENTORY_TYPE.INVENTORY_SEARCH]
  }
  return delivery_range
}

/**
 * @description: 为计划分配落地页参数
 * @param {*} planList
 * @param {*} landingPages
 * @return {*}
 */
function appendLandingPages(planList, { landingPages }) {
  if (!landingPages.length) return planList
  return planList.map((plan, index) => {
    const dupPlan = clone(plan)
    const landingIndex = index % landingPages.length
    const web_url_material_list = landingPages[landingIndex].contents?.slice(0, 10) || []
    const packName = landingPages[landingIndex].packName
    dupPlan.creative_params.web_url_pack_name = packName
    if (web_url_material_list?.length) {
      dupPlan.creative_params.web_url_material_list = web_url_material_list
    }
    return dupPlan
  })
}

/**
 * @description: 分配账号下的计划，根据创意分配到每个账号：每个账号下的创意组都是相同的
 * @param {*} planList
 * @param {*} creatives
 * @return {*}
 */
function distributeByCreative(planGroups, targetings, { accounts }) {
  const [planGroup] = planGroups
  const creativeGroups = planGroup.map((plan) => plan.creative_params)

  const plansGroupByAccount = {}

  // 分配创意组到每个账号
  for (let i = 0; i < planGroups.length; i++) {
    const planGroup = planGroups[i]
    const account = accounts[i % accounts.length]
    if (!plansGroupByAccount[account.id]) {
      plansGroupByAccount[account.id] = []
    }

    const withAccountList = planGroup.map((plan) => {
      return { ...plan, ad_account_id: account.id, ad_account_simple_name: account.adAccountSimpleName }
    })
    plansGroupByAccount[account.id].push(...withAccountList)
  }
  const groupByAccountList = Object.values(plansGroupByAccount)

  return plansGroupByAccount
}

/**
 * @description: 分配账号下的计划，根据定向分配到每个账号：每个账号下的定向包都是相同的
 */
function distributeByTargeting(planList, { accounts }) {
  const plansGroupByAccount = {}

  let planListGroupByTargeting = groupBy(planList, (plan) => plan.targetings.targetingName)
  planListGroupByTargeting = Object.values(planListGroupByTargeting)

  if (planListGroupByTargeting.length > accounts.length) {
    for (let i = 0; i < planListGroupByTargeting.length; i++) {
      const account = accounts[i % accounts.length]
      const group = planListGroupByTargeting[i]
      const targetingId = group[0].targetings.caohuaTargetingId

      if (!plansGroupByAccount[targetingId]) {
        plansGroupByAccount[targetingId] = []
      }
      plansGroupByAccount[targetingId] = plansGroupByAccount[targetingId].concat(
        group.map((plan) => ({ ...plan, ad_account_id: account.id, ad_account_simple_name: account.adAccountSimpleName }))
      )
    }
  } else {
    for (let i = 0; i < accounts.length; i++) {
      const account = accounts[i]
      const accountId = account.id
      const plans = planListGroupByTargeting[i % planListGroupByTargeting.length]
      if (!plansGroupByAccount[accountId]) {
        plansGroupByAccount[accountId] = []
      }
      plansGroupByAccount[accountId] = plansGroupByAccount[accountId].concat(
        plans.map((plan) => ({ ...plan, ad_account_id: account.id, ad_account_simple_name: account.adAccountSimpleName }))
      )
    }
  }

  // 再次根据账号分组
  const newPlanList = setProjectInfo(Object.values(plansGroupByAccount))
  const newPlansGroupByAccount = {}

  for (let i = 0; i < newPlanList.length; i++) {
    const plan = newPlanList[i]
    if (!newPlansGroupByAccount[plan.ad_account_id]) {
      newPlansGroupByAccount[plan.ad_account_id] = []
    }
    newPlansGroupByAccount[plan.ad_account_id] = newPlansGroupByAccount[plan.ad_account_id].concat(plan)
  }

  return newPlansGroupByAccount
}

/**
 * @description: 生成定向包全复制到到账号的组合
 *  分配示例：
        账号 定向包 创意组
        A1   B1    C1
        A1   B2    C2
        A1   B3    C3
        A1   B4    C4
        A2   B1    C5
        A2   B2    C6
        A2   B3    C7
        A2   B4    C8
        A1   B1    C9
        A1   B2    C10
 * @return {*}
 */

function generatePlanCombinationsByDuplicate(planList, accounts, targetings) {
  const combinations = []

  for (let i = 0; i < planList.length; i++) {
    // 计算当前应该使用哪个账号
    // 每个账号连续使用所有定向包后才换下一个账号
    const accountGroupIndex = Math.floor(i / targetings.length) % accounts.length
    const account = accounts[accountGroupIndex]

    // 计算当前应该使用哪个定向包
    // 在每个账号内，定向包按顺序循环使用
    const targetingIndex = i % targetings.length
    const targeting = targetings[targetingIndex]

    const combination = {
      account,
      targeting,
      plan: planList[i]
    }

    combinations.push(combination)
  }
  return combinations
}

/**
 * @description: 生成账号和定向包的组合
 分配示例：
        账号 定向包 创意组
        A1    B1    C1
        A2    B2    C2
        A1    B3    C3
        A2    B4    C4
        A1    B5    C5
        A1    B1    C6
        A2    B2    C7
        A1    B3    C8
 * @param {*} planList
 * @param {*} accounts
 * @param {*} targetings
 * @return {*}
 */
function generatePlanCombinations(planList, accounts, targetings) {
  const combinations = []

  // 首先创建账号和定向包的基础组合
  const baseMapping = []
  let needleIndex = 0

  if (targetings.length > accounts.length) {
    // 创建初始的账号-定向包基准映射
    for (let i = 0; i < targetings.length; i++) {
      baseMapping.push({
        account: accounts[needleIndex % accounts.length],
        targeting: targetings[i]
      })
      needleIndex++
    }
  } else {
    // 创建初始的账号-账号基准映射
    for (let i = 0; i < accounts.length; i++) {
      baseMapping.push({
        account: accounts[i],
        targeting: targetings[needleIndex % targetings.length]
      })
      needleIndex++
    }
  }

  // 为每个创意组生成完整组合
  for (let i = 0; i < planList.length; i++) {
    // 使用baseMapping中的组合，循环使用
    const baseCombination = baseMapping[i % baseMapping.length]
    const combination = {
      account: baseCombination.account,
      targeting: baseCombination.targeting,
      plan: planList[i]
    }
    combinations.push(combination)
  }

  return combinations
}

function distributeByCreativeGroup(type, planList, { accounts, targetings }) {
  const results =
    type === 2
      ? generatePlanCombinations(planList, accounts, targetings)
      : generatePlanCombinationsByDuplicate(planList, accounts, targetings)

  const newPlanList = []
  const projectNames = {}
  const planNames = {}
  const planIndex = 0

  for (let i = 0; i < results.length; i++) {
    const { account, targeting, plan } = results[i]
    const dupPlan = clone(plan)
    const delivery_range = buildDeliveryRange(targeting, dupPlan)
    const { ad_type, delivery_mode, delivery_type } = dupPlan.outer_plan_params
    const isV3 = dupPlan.inner_plan_params.media_version === 'TOUTIAO3.0'
    const delivery = ad_type === AD_TYPE.SEARCH
      ? (isV3 ? DELIVERY_TYPE.NORMAL : delivery_type)
      : delivery_mode
    const targetingParam = filterTargetingParams(targeting.targetingParamJson, ad_type, delivery, isV3)
    dupPlan.ad_account_id = account.id
    dupPlan.outer_plan_params = {
      ...dupPlan.outer_plan_params,
      delivery_range
    }
    dupPlan.targetings = {
      caohuaTargetingId: targeting.targetingId,
      // 定向名称，用于预览表格显示
      targetingName: targeting.targetingName,
      targetingParam
    }

    const key = `${dupPlan.ad_account_id}_${targeting.targetingName}`
    const projectIndex = i + 1
    const name = dupPlan.campaign_params.project_name
    const permanent_plan_name = `${name}_${projectIndex}`
    const project_name = replaceDynamicValue(name, dupPlan).concat(`_${projectIndex}`)

    // 为同账号同定向的计划保存permanent_plan_name，permanent_plan_name用于合并单元格
    if (!planNames[key]) {
      planNames[key] = permanent_plan_name
      // 禁止第一条计划被删除、被取消勾选
      dupPlan.planRowDisabled = true
    }
    dupPlan.temp_project_name = planNames[key]
    // 为同账号同定向的计划保存project_name
    if (!projectNames[key]) {
      projectNames[key] = project_name
    }
    // 同账号同定向的计划的项目名称必须相同
    dupPlan.campaign_params.project_name = projectNames[key]
    dupPlan.inner_plan_params.plan_name = projectNames[key]
    newPlanList.push(dupPlan)
  }

  const planGroup = groupBy(newPlanList, (plan) => plan.temp_project_name)

  // 为每个项目下的计划名称添加递增索引
  for (const key in planGroup) {
    const group = planGroup[key]
    for (let i = 0; i < group.length; i++) {
      if (i > 0) {
        group[i].inner_plan_params.plan_name = `${group[0].inner_plan_params.plan_name}_${i}`
      }
    }
  }

  const list = Object.values(planGroup).flat()

  // 再次根据账号分组
  const newPlansGroupByAccount = {}

  for (let i = 0; i < list.length; i++) {
    const plan = list[i]
    if (!newPlansGroupByAccount[plan.ad_account_id]) {
      newPlansGroupByAccount[plan.ad_account_id] = []
    }

    // 最终根据账号分配的数组项需要根据permanent_plan_name进行排序， 使相同permanent_plan_name的计划排在一起，否则在表格中合并单元格会错乱
    newPlansGroupByAccount[plan.ad_account_id] = newPlansGroupByAccount[plan.ad_account_id].concat(plan).sort((a, b) => {
      return a.temp_project_name.localeCompare(b.temp_project_name)
    })
  }
  return newPlansGroupByAccount
}

/**
 * @description: 分配账号下的计划，每个账号的计划全部相同
 * @return {*}
 */
function distributeByDupItemToAccount(planGroups, { accounts }) {
  const plansGroupByAccount = {}

  for (let i = 0; i < accounts.length; i++) {
    const account = accounts[i]

    planGroups = planGroups.map((group) => {
      return group.map((plan) => {
        return { ...plan, ad_account_id: account.id, ad_account_simple_name: account.adAccountSimpleName }
      })
    })

    const planList = setProjectInfo(planGroups, i * planGroups.length, i * planGroups.length)

    plansGroupByAccount[account.id] = planList.map((plan) => ({ ...plan }))
  }

  return plansGroupByAccount
}

/**
 * @description: 分配账号下的状态
 * @return {*}
 */
function tableStatesFormToAccount({ accounts }) {
  const tableStatesByAccount = {}
  for (let i = 0; i < accounts.length; i++) {
    tableStatesByAccount[accounts[i].id] = {
      projectSwitch: true,
      planSwitch: true,
      isCheckAll: true
    }
  }
  return tableStatesByAccount
}
/**
 * @description: 根据项目名称对广告分组
 * @param {*} planGroups
 * @param {*} projectIndex 为确保每个项目名称唯一，为项目名称添加递增索引
 * @param {*} planIndex 为确保每个项目下的计划名称唯一，为计划名称添加递增索引
 * @return {*}
 */
function setProjectInfo(planGroups, projectIndex = 0, planIndex = 0) {
  const planList = []
  for (let j = 0; j < planGroups.length; j++) {
    projectIndex = projectIndex + 1

    // 同项目下的计划的项目名称必须相同，不同的项目使用projectIndex递增
    const group = planGroups[j].map((item) => {
      const name = item.campaign_params.project_name
      const temp_project_name = `${name}_${projectIndex}`
      const project_name = replaceDynamicValue(name, item).concat(`_${projectIndex}`)

      return {
        ...item,
        campaign_params: { project_name },
        // 保存临时项目名称
        temp_project_name
      }
    })

    for (let k = 0; k < group.length; k++) {
      const plan = clone(group[k])
      const { project_name } = plan.campaign_params

      // 每个项目的第一条计划的计划名称必须与项目名称一致
      if (k === 0) {
        plan.inner_plan_params.plan_name = project_name
        // 禁止第一条计划被删除、被取消勾选
        plan.planRowDisabled = true
      } else {
        planIndex = planIndex + 1
        // 其他的计划名称使用planIndex递增
        plan.inner_plan_params.plan_name = `${project_name}_${planIndex}`
        // 其他计划的项目名称始终与当前组的第一个项的project_name保持一致，解决设置了广告组名动态词项目组名不一致问题
        plan.campaign_params.project_name = group[0].campaign_params.project_name
      }

      planList.push({ ...plan })
    }
  }

  return planList
}

function replaceDynamicValue(name, item) {
  const { ad_account_simple_name, targetings, creative_params } = item
  const replaceDynamicName = name.replace(/{(.*?)}/g, (res) => {
    const dynamicWord = res.replace(/[{}]/g, '')
    let value = ''
    switch (dynamicWord) {
      case '日期':
        value = moment().format('MMDD')
        break
      case '时间':
        value = moment().format('HH:mm:ss')
        break
      case '项目组名':
        value = targetings?.targetingName || ''
        break
      case '账号简称':
        value = ad_account_simple_name || ''
        break
      case '计划组名':
        value = creative_params?.campaign_name || ''
        break
      default:
        break
    }
    return value ? '_'.concat(value) : ''
  })
  return replaceDynamicName
}

/**
 * @description: 设置账号信息
 * @param {*} planList
 * @param {*} planIndex
 * @param {*} account
 * @return {*}
 */
function setAccountParams(planList, planIndex, account) {
  return planList.map((plan, index) => {
    const dupPlan = clone(plan)
    // const plan_name = plan.inner_plan_params.plan_name + `_${++planIndex}`

    dupPlan.ad_account_id = account.id
    // dupPlan.inner_plan_params.plan_name = plan_name
    // dupPlan.outer_plan_params.name = plan_name

    return dupPlan
  })
}

/**
 * @description: 根据定向包分配项目
 * @param {*} planList
 * @return {*}
 */
function buildProjectGroupByTargeting(planList) {
  const groups = groupBy(planList, (plan) => plan.targetings.caohuaTargetingId)

  let index = 0
  const newPlanList = []
  for (const key in groups) {
    const group = groups[key]
    index += 1
    for (let i = 0; i < group.length; i++) {
      const project_name = `${group[i].campaign_params.project_name}_${index}`
      group[i].campaign_params = { project_name }
      group[i].temp_project_name = project_name

      // 每个项目的第一条计划的计划名称必须与项目名称一致
      if (i === 0) {
        group[i].inner_plan_params.plan_name = project_name
      }
      // else {
      //   group[i].inner_plan_params.plan_name += `_${index}_${i}`
      // }

      newPlanList.push(group[i])
    }
  }
  return newPlanList
}
